import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  FolderOpen, Plus, Search, Eye, Edit, Trash2, RefreshCw,
  Building2, Tag, DollarSign, AlertCircle, Users,
  X, Calendar, CreditCard, CheckCircle, Clock,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import pb from '../../lib/pocketbaseClient';
import { useAuth } from '../../contexts/AuthContext';

// ── Helpers ──
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try { return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return '—'; }
};

const formatPrice = (price) => {
  if (!price) return '₹0';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(price);
};

const getTaskStatusBadge = (status) => {
  const config = {
    pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700' },
    assigned: { label: 'Assigned', color: 'bg-blue-100 text-blue-700' },
    'in-progress': { label: 'In Progress', color: 'bg-indigo-100 text-indigo-700' },
    submitted: { label: 'Submitted', color: 'bg-purple-100 text-purple-700' },
    completed: { label: 'Completed', color: 'bg-green-100 text-green-700' },
  };
  return config[status] || { label: status || 'Unknown', color: 'bg-slate-100 text-slate-700' };
};

// ── Main Component ──────────────────────────────────────────────
const Projects = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // ── Fetch projects ──
  const { data: projects = [], isLoading, refetch } = useQuery({
    queryKey: ['projects', 'all'],
    queryFn: () =>
      pb.collection('projects').getFullList({
        sort: '-created',
        expand: 'leadId,leadId.assignedSales',
      }),
  });

  // ── Fetch project details when modal opens ──
  const { data: projectDetail, refetch: refetchDetail } = useQuery({
    queryKey: ['projectDetail', selectedProject?.id],
    queryFn: () => {
      if (!selectedProject) return null;
      return pb.collection('projects').getOne(selectedProject.id, {
        expand: 'leadId',
      });
    },
    enabled: !!selectedProject,
  });

  const { data: projectTasks = [] } = useQuery({
    queryKey: ['projectTasks', selectedProject?.id],
    queryFn: () => {
      if (!selectedProject) return [];
      return pb.collection('project_tasks').getFullList({
        filter: `projectId = "${selectedProject.id}"`,
        sort: 'paymentTermIndex',
        expand: 'assignedTo',
      });
    },
    enabled: !!selectedProject,
  });

  const { data: projectPayments = [] } = useQuery({
    queryKey: ['projectPayments', selectedProject?.id],
    queryFn: () => {
      if (!selectedProject) return [];
      return pb.collection('payments').getFullList({
        filter: `projectId = "${selectedProject.id}"`,
        sort: 'termIndex',
      });
    },
    enabled: !!selectedProject,
  });

  // ── Filter projects ──
  const filtered = projects.filter(p =>
    p.name?.toLowerCase().includes(search.toLowerCase()) ||
    p.expand?.leadId?.name?.toLowerCase().includes(search.toLowerCase()) ||
    p.packageType?.toLowerCase().includes(search.toLowerCase())
  );

  // ── Open modal ──
  const handleView = (project) => {
    setSelectedProject(project);
    setShowDetailModal(true);
    // Refetch details when modal opens
    setTimeout(() => refetchDetail(), 100);
  };

  return (
    <div className="space-y-6">
      {/* ─── Header ─────────────────────────────────────────── */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 p-8 shadow-xl">
        <div className="absolute top-0 right-0 -mt-16 -mr-16 w-64 h-64 bg-white/10 rounded-full blur-2xl" />
        <div className="absolute bottom-0 left-0 -mb-16 -ml-16 w-48 h-48 bg-white/10 rounded-full blur-2xl" />
        <div className="relative flex flex-col sm:flex-row justify-between items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-2">
              <FolderOpen className="w-8 h-8" /> Projects
            </h1>
            <p className="text-white/80">Manage all client projects</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => refetch()} className="bg-white/10 backdrop-blur-sm text-white border-white/30 hover:bg-white/20">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* ─── Search ─────────────────────────────────────────── */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search by project name, client, package..."
          className="pl-9"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* ─── Table ──────────────────────────────────────────── */}
      <Card className="border-0 shadow-xl overflow-hidden">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader className="bg-gradient-to-r from-indigo-50 to-purple-50">
              <TableRow>
                <TableHead className="text-indigo-700 font-semibold">Project</TableHead>
                <TableHead className="text-indigo-700 font-semibold">Client</TableHead>
                <TableHead className="text-indigo-700 font-semibold">Package</TableHead>
                <TableHead className="text-indigo-700 font-semibold">Building</TableHead>
                <TableHead className="text-indigo-700 font-semibold">Amount</TableHead>
                <TableHead className="text-indigo-700 font-semibold">Status</TableHead>
                <TableHead className="text-indigo-700 font-semibold">Created</TableHead>
                <TableHead className="text-indigo-700 font-semibold text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8">Loading...</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">No projects found</TableCell></TableRow>
              ) : (
                filtered.map((project) => {
                  const client = project.expand?.leadId;
                  return (
                    <TableRow key={project.id} className="hover:bg-indigo-50/50 transition-colors">
                      <TableCell className="font-medium">{project.name}</TableCell>
                      <TableCell>
                        <Link to={`/office/clients/${client?.id}`} className="text-indigo-600 hover:underline">
                          {client?.name || '—'}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                          {project.packageType || '—'}
                        </Badge>
                      </TableCell>
                      <TableCell>{project.buildingType || '—'}</TableCell>
                      <TableCell className="font-bold text-amber-600">
                        {formatPrice(project.finalAmount || project.totalAmount)}
                      </TableCell>
                      <TableCell>
                        <Badge className={project.status === 'Completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                          {project.status || 'Planning'}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(project.created)}</TableCell>
                      <TableCell className="text-center">
                        <div className="flex gap-1 justify-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleView(project)}
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            asChild
                            title="Go to Client"
                          >
                            <Link to={`/office/clients/${client?.id}`}>
                              <Users className="w-4 h-4" />
                            </Link>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ─── Project Detail Modal ──────────────────────────── */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="flex justify-between items-center">
            <DialogTitle className="text-2xl font-bold text-indigo-700">
              {projectDetail?.name || 'Project Details'}
            </DialogTitle>
            <Button variant="ghost" size="sm" onClick={() => setShowDetailModal(false)}>
              <X className="w-5 h-5" />
            </Button>
          </DialogHeader>

          {!projectDetail ? (
            <div className="flex justify-center items-center h-40">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Project Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500">Client</p>
                  <p className="font-medium">
                    {projectDetail.expand?.leadId?.name || '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Status</p>
                  <Badge className={projectDetail.status === 'Completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                    {projectDetail.status || 'Planning'}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Package</p>
                  <p className="font-medium">{projectDetail.packageType || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Building Type</p>
                  <p className="font-medium">{projectDetail.buildingType || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Total Amount</p>
                  <p className="font-bold text-amber-600">{formatPrice(projectDetail.totalAmount)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Discount</p>
                  <p className="font-medium">{projectDetail.discount || 0}%</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Final Amount</p>
                  <p className="font-bold text-green-600">{formatPrice(projectDetail.finalAmount)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Progress</p>
                  <div className="flex items-center gap-2">
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div
                        className="bg-indigo-500 h-2 rounded-full transition-all"
                        style={{ width: `${Math.min(projectDetail.progress || 0, 100)}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium">{projectDetail.progress || 0}%</span>
                  </div>
                </div>
              </div>

              {projectDetail.description && (
                <div>
                  <p className="text-xs text-slate-500">Description</p>
                  <p className="text-sm">{projectDetail.description}</p>
                </div>
              )}

              {/* Payment Terms */}
              <div>
                <h4 className="font-semibold text-slate-800 flex items-center gap-2 mb-2">
                  <CreditCard className="w-4 h-4 text-amber-500" /> Payment Terms
                </h4>
                {projectDetail.paymentTerms && projectDetail.paymentTerms.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {projectDetail.paymentTerms.map((term, idx) => {
                      const isPaid = projectPayments.some(
                        p => p.termIndex === term.term && p.status === 'Completed'
                      );
                      return (
                        <div key={idx} className="flex justify-between items-center p-3 border rounded-lg">
                          <div>
                            <p className="font-medium">Term {term.term}</p>
                            <p className="text-sm text-slate-500">{formatPrice(term.amount)}</p>
                          </div>
                          <Badge className={isPaid ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                            {isPaid ? '✅ Paid' : 'Pending'}
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">No payment terms defined.</p>
                )}
                <div className="mt-2 p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm font-medium">
                    Total Paid:{' '}
                    <span className="text-green-600">
                      {formatPrice(projectPayments.filter(p => p.status === 'Completed').reduce((sum, p) => sum + p.amount, 0))}
                    </span>
                  </p>
                </div>
              </div>

              {/* Tasks */}
              <div>
                <h4 className="font-semibold text-slate-800 flex items-center gap-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-purple-500" /> Tasks ({projectTasks.length})
                </h4>
                {projectTasks.length === 0 ? (
                  <p className="text-sm text-slate-400">No tasks created yet.</p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {projectTasks.map((task) => {
                      const status = getTaskStatusBadge(task.status);
                      const assigned = task.expand?.assignedTo?.name || 'Unassigned';
                      return (
                        <div key={task.id} className="flex flex-wrap justify-between items-center p-3 border rounded-lg hover:bg-slate-50">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm">{task.title}</p>
                            <p className="text-xs text-slate-500">
                              Term {task.paymentTermIndex || '—'} • {task.category || 'General'} • Assigned: {assigned}
                            </p>
                          </div>
                          <Badge className={`${status.color} text-xs`}>
                            {status.label}
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowDetailModal(false)}>Close</Button>
                <Button
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  onClick={() => {
                    const clientId = projectDetail.expand?.leadId?.id;
                    if (clientId) {
                      navigate(`/office/clients/${clientId}`);
                    } else {
                      navigate('/office/projects');
                    }
                    setShowDetailModal(false);
                  }}
                >
                  <Users className="w-4 h-4 mr-1" /> Go to Client
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Projects;